import { BlobServiceClient } from "@azure/storage-blob";
import APP_CONFIG from "../config/appConfig";

// ==================== CONFIGURATION ====================

const AZURE_CONFIG = {
  storageAccountName: APP_CONFIG.AZURE_STORAGE_ACCOUNT_NAME,
  containerName: APP_CONFIG.AZURE_CONTAINER_NAME,
  sasToken: APP_CONFIG.AZURE_SAS_TOKEN,
};

interface UploadOptions {
  addTimestamp?: boolean;
  preserveOriginalName?: boolean;
  customExtension?: string;
  onProgress?: (progress: any) => void;
  cacheControl?: string;
  maxRetries?: number;
  retryDelay?: number;
}

interface UploadResult {
  url: string;
  filename: string;
  success: boolean;
  error?: string;
  size?: number;
  type?: string;
}

interface AzureConfig {
  storageAccountName: string;
  containerName: string;
  sasToken: string;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Generate filename based on options
 */
function generateFileName(
  file: File,
  baseFilename: string,
  options: {
    addTimestamp?: boolean;
    preserveOriginalName?: boolean;
    customExtension?: string;
    attempt?: number;
  } = {}
): string {
  const { addTimestamp, preserveOriginalName, customExtension, attempt } = options;

  let finalName: string;
  let extension: string;

  if (preserveOriginalName) {
    const nameParts = file.name.split('.');
    finalName = nameParts.slice(0, -1).join('.');
    extension = nameParts[nameParts.length - 1] || '';
  } else {
    finalName = baseFilename;
    extension = customExtension || file.name.split('.').pop() || '';
  }

  // ENHANCED SANITIZATION FOR AZURE BLOB STORAGE
  // Replace spaces and special characters with underscores
  finalName = finalName
    .replace(/\s+/g, '_')           // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_-]/g, '_') // Replace special chars with underscores
    .replace(/_{2,}/g, '_')         // Replace multiple underscores with single
    .replace(/^_|_$/g, '');         // Remove leading/trailing underscores

  // Sanitize extension
  extension = extension
    .replace(/[^a-zA-Z0-9]/g, '')   // Keep only alphanumeric chars in extension
    .toLowerCase();                 // Convert to lowercase

  // Ensure filename is not empty
  if (!finalName) {
    finalName = 'file';
  }

  // Add timestamp if requested
  if (addTimestamp) {
    const timestamp = Date.now();
    finalName = `${finalName}_${timestamp}`;
  }

  // Add attempt number for retries
  if (attempt && attempt > 1) {
    finalName = `${finalName}_retry${attempt}`;
  }

  const result = `${finalName}.${extension}`;
  console.log('📝 Original filename:', file.name);
  console.log('📝 Sanitized filename:', result);

  return result;
}

/**
 * Build blob path
 */
function buildBlobPath(
  container: string,
  subcontainer?: string | number,
  filename?: string
): string {
  const parts: string[] = [];

  // if (container) {
  //   // Sanitize container name
  //   const cleanContainer = container.replace(/[^a-zA-Z0-9_-]/g, '_');
  //   parts.push(cleanContainer);
  // }

  // if (subcontainer !== undefined && subcontainer !== null && subcontainer !== '') {
  //   // Sanitize subcontainer name
  //   const cleanSubcontainer = String(subcontainer).replace(/[^a-zA-Z0-9_-]/g, '_');
  //   parts.push(cleanSubcontainer);
  // }

  // if (filename) {
  //   // Filename should already be sanitized by generateFileName
  //   parts.push(filename);
  // }
  if (subcontainer) {
    parts.push(String(subcontainer).replace(/[^a-zA-Z0-9_-]/g, "_"));
  }

  if (filename) {
    parts.push(filename);
  }

  const result = parts.join('/');
  console.log('🛤️ Clean blob path:', result);

  return result;
}

/**
 * Validate Azure configuration
 */
function validateConfig(config: AzureConfig): void {
  if (!config.storageAccountName || !config.containerName || !config.sasToken) {
    throw new Error('Azure configuration is incomplete. Please check storageAccountName, containerName, and sasToken.');
  }
}

/**
 * Log configuration status
 */
function logConfigStatus(): void {
  console.log('🔍 Azure Configuration Status:');
  console.log('  Storage Account:', AZURE_CONFIG.storageAccountName || 'NOT SET');
  console.log('  Container Name:', AZURE_CONFIG.containerName || 'NOT SET');
  console.log('  Has SAS Token:', !!AZURE_CONFIG.sasToken);
}

// ==================== CORE UPLOAD FUNCTIONS ====================

/**
 * Main upload function - fully configurable
 * @param file - File to upload
 * @param filename - Base filename (without extension)
 * @param container - Container path
 * @param subcontainer - Subcontainer path (optional)
 * @param options - Upload options (optional)
 * @param customConfig - Custom Azure config (optional, uses default if not provided)
 * @returns Promise<string> - Returns the uploaded file URL
 */
export async function uploadImage(
  file: File,
  filename: string,
  container: string,
  subcontainer?: string | number,
  options: UploadOptions = {},
  customConfig?: AzureConfig
): Promise<string> {

  if (!file) {
    throw new Error('No file provided');
  }

  if (!filename || !container) {
    throw new Error('Filename and container are required');
  }

  // Use custom config or default
  const config = customConfig || AZURE_CONFIG;
  validateConfig(config);

  const {
    addTimestamp = true,
    preserveOriginalName = false,
    customExtension,
    onProgress = (ev) => console.log(`Upload progress: ${ev.loadedBytes}/${file.size} bytes`),
    cacheControl = 'public, max-age=31536000',
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  let lastError: Error;

  // Retry logic
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Upload attempt ${attempt}/${maxRetries} for file: ${filename}`);
      console.log(`📄 Original file name: ${file.name}`);

      // Generate final filename with enhanced sanitization
      const finalFilename = generateFileName(file, filename, {
        addTimestamp: addTimestamp || attempt > 1,
        preserveOriginalName,
        customExtension,
        attempt
      });

      // Build blob path with sanitized components
      const blobPath = buildBlobPath(container, subcontainer, finalFilename);

      // Construct URLs
      // const baseUrl = `https://${config.storageAccountName}.blob.core.windows.net/${config.containerName}`;
      // const uploadUrl = `${baseUrl}/${blobPath}?${config.sasToken}`;
      const uploadUrl =
        `https://${config.storageAccountName}.blob.core.windows.net/` +
        `${config.containerName}/${blobPath}?${config.sasToken}`;

      // const finalUrl = `${uploadUrl}/${blobPath}`;
      const finalUrl =
        `https://${config.storageAccountName}.blob.core.windows.net/` +
        `${config.containerName}/${blobPath}`;

      console.log('🎯 Upload URL:', uploadUrl);
      console.log('📁 Final URL (clean):', finalUrl);

      // Validate that the final URL doesn't have spaces
      if (finalUrl.includes(' ')) {
        throw new Error(`Generated URL contains spaces: ${finalUrl}`);
      }

      // Upload using fetch
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
          'x-ms-blob-cache-control': cacheControl
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      console.log(`✅ Upload successful: ${finalFilename}`);
      console.log(`🔍 Backend will receive clean URL: ${finalUrl}`);

      // Test the final URL against backend validation
      if (typeof validateUrlForBackend === 'function') {
        const passesValidation = validateUrlForBackend(finalUrl);
        console.log(`🔍 Backend validation test: ${passesValidation ? 'PASS' : 'FAIL'}`);
      }

      return finalUrl;

    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown upload error');
      console.error(`❌ Upload attempt ${attempt} failed:`, lastError.message);

      if (attempt < maxRetries) {
        const delay = retryDelay * attempt;
        console.log(`⏳ Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw new Error(`Failed to upload file after ${maxRetries} attempts: ${lastError!.message}`);
}

/**
 * Backward compatibility function for existing uploadFileToBlob calls
 * This function maintains compatibility with your existing code
 */
export async function uploadFileToBlob(
  file: File,
  filename: string,
  container: string,
  subcontainer: string | number,
  customConfig?: AzureConfig
): Promise<string> {
  console.log('📝 uploadFileToBlob called with:', {
    filename,
    container,
    subcontainer,
    fileType: file.type,
    fileName: file.name
  });

  return uploadImage(file, filename, container, subcontainer, {
    addTimestamp: true,
    maxRetries: 3
  }, customConfig);
}

/**
 * Upload function that returns detailed result object
 */
export async function uploadImageWithResult(
  file: File,
  filename: string,
  container: string,
  subcontainer?: string | number,
  options: UploadOptions = {},
  customConfig?: AzureConfig
): Promise<UploadResult> {

  try {
    const url = await uploadImage(file, filename, container, subcontainer, options, customConfig);

    return {
      url,
      filename: generateFileName(file, filename, options),
      success: true,
      size: file.size,
      type: file.type
    };
  } catch (error) {
    return {
      url: '',
      filename: '',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      size: file.size,
      type: file.type
    };
  }
}

// ==================== VALIDATION FUNCTIONS ====================

/**
 * Validate file before upload
 */
export function validateFile(file: File, options: {
  maxSize?: number; // in bytes
  allowedTypes?: string[];
  maxDimensions?: { width: number; height: number };
} = {}): Promise<{ isValid: boolean; error?: string }> {

  return new Promise((resolve) => {
    const { maxSize, allowedTypes, maxDimensions } = options;

    // Check file size
    if (maxSize && file.size > maxSize) {
      resolve({
        isValid: false,
        error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size ${(maxSize / 1024 / 1024).toFixed(2)}MB`
      });
      return;
    }

    // Check file type
    if (allowedTypes && allowedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isTypeAllowed = allowedTypes.some(type =>
        type.toLowerCase() === fileExtension ||
        file.type.toLowerCase().includes(type.toLowerCase())
      );

      if (!isTypeAllowed) {
        resolve({
          isValid: false,
          error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
        });
        return;
      }
    }

    // Check image dimensions (for image files)
    if (maxDimensions && file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        if (img.width > maxDimensions.width || img.height > maxDimensions.height) {
          resolve({
            isValid: false,
            error: `Image dimensions ${img.width}x${img.height} exceed maximum allowed ${maxDimensions.width}x${maxDimensions.height}`
          });
        } else {
          resolve({ isValid: true });
        }
      };
      img.onerror = () => {
        resolve({
          isValid: false,
          error: 'Invalid image file'
        });
      };
      img.src = URL.createObjectURL(file);
    } else {
      resolve({ isValid: true });
    }
  });
}

/**
 * Backend validation compatibility check
 */
export function validateUrlForBackend(url: string): boolean {
  console.log('🔍 Testing backend validation compatibility for:', url);

  const backendRegex = new RegExp(
    APP_CONFIG.AZURE_VALIDATION_DOMAIN_REGEX,
    "i"
  );

  const passes = backendRegex.test(url);
  console.log('✅ Backend validation result:', passes ? 'PASS' : 'FAIL');

  return passes;
}

// ==================== CONVENIENCE FUNCTIONS ====================

/**
 * Upload template images (logos, etc.)
 */
export const uploadTemplateImage = async (file: File, logoType: string, customConfig?: AzureConfig): Promise<string> => {
  return uploadImage(file, logoType, 'system_settings', logoType, {
    addTimestamp: true,
    maxRetries: 3
  }, customConfig);
};

/**
 * Upload profile images
 */
export const uploadProfileImage = async (file: File, userId: string | number, customConfig?: AzureConfig): Promise<string> => {
  return uploadImage(file, 'profile_picture', 'profiles', userId, {
    addTimestamp: true,
    maxRetries: 3
  }, customConfig);
};

/**
 * Upload documents with original filename preserved
 */
export const uploadDocument = async (file: File, documentType: string, userId?: string | number, customConfig?: AzureConfig): Promise<string> => {
  return uploadImage(file, documentType, 'documents', userId || 'general', {
    preserveOriginalName: true,
    addTimestamp: true,
    maxRetries: 3
  }, customConfig);
};

/**
 * Upload with validation
 */
export const uploadWithValidation = async (
  file: File,
  filename: string,
  container: string,
  subcontainer?: string | number,
  validationOptions?: {
    maxSize?: number;
    allowedTypes?: string[];
    maxDimensions?: { width: number; height: number };
  },
  uploadOptions?: UploadOptions,
  customConfig?: AzureConfig
): Promise<string> => {

  // Validate file first
  const validation = await validateFile(file, validationOptions);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Upload if validation passes
  return uploadImage(file, filename, container, subcontainer, uploadOptions, customConfig);
};

/**
 * Legacy function for backward compatibility
 */
export async function uploadPlotShape(file: File, filename: string, container: string, customConfig?: AzureConfig): Promise<string> {
  if (!file) return '';

  return uploadImage(file, filename, container, undefined, {
    addTimestamp: true,
    maxRetries: 3
  }, customConfig);
}

// ==================== BULK UPLOAD FUNCTIONS ====================

/**
 * Upload multiple files
 */
export async function uploadMultipleFiles(
  files: File[],
  container: string,
  subcontainer?: string | number,
  options: UploadOptions & {
    onFileComplete?: (index: number, result: UploadResult) => void;
    onAllComplete?: (results: UploadResult[]) => void;
  } = {},
  customConfig?: AzureConfig
): Promise<UploadResult[]> {

  const { onFileComplete, onAllComplete, ...uploadOptions } = options;

  const uploadPromises = files.map(async (file, index) => {
    try {
      const filename = `file_${index}_${Date.now()}`;
      const result = await uploadImageWithResult(file, filename, container, subcontainer, {
        ...uploadOptions,
        preserveOriginalName: true,
        addTimestamp: true
      }, customConfig);

      if (onFileComplete) {
        onFileComplete(index, result);
      }

      return result;
    } catch (error) {
      const errorResult: UploadResult = {
        url: '',
        filename: file.name,
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
        size: file.size,
        type: file.type
      };

      if (onFileComplete) {
        onFileComplete(index, errorResult);
      }

      return errorResult;
    }
  });

  const results = await Promise.all(uploadPromises);

  if (onAllComplete) {
    onAllComplete(results);
  }

  return results;
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Test Azure connectivity
 */
export async function testAzureConnectivity(customConfig?: AzureConfig): Promise<boolean> {
  try {
    const config = customConfig || AZURE_CONFIG;
    validateConfig(config);

    console.log('🔍 Testing Azure connectivity...');

    const testUrl = `https://${config.storageAccountName}.blob.core.windows.net/${config.containerName}?${config.sasToken}`;
    const response = await fetch(testUrl, { method: 'HEAD' });

    const isConnected = response.ok;
    console.log(isConnected ? '✅ Azure connectivity test passed' : '❌ Azure connectivity test failed');

    return isConnected;
  } catch (error) {
    console.error('❌ Azure connectivity test failed:', error);
    return false;
  }
}

/**
 * Debug configuration
 */
export function debugConfiguration(): void {
  logConfigStatus();

  if (!AZURE_CONFIG.storageAccountName || !AZURE_CONFIG.containerName || !AZURE_CONFIG.sasToken) {
    console.warn('⚠️ Configuration incomplete. Update AZURE_CONFIG at the top of azure-storage-blob.ts');
  } else {
    console.log('✅ Configuration looks complete');
  }
}

/**
 * Update configuration at runtime (optional)
 */
export function updateAzureConfig(newConfig: Partial<AzureConfig>): void {
  Object.assign(AZURE_CONFIG, newConfig);
  console.log('🔧 Azure configuration updated');
  logConfigStatus();
}

/**
 * Get current configuration
 */
export function getAzureConfig(): AzureConfig {
  return { ...AZURE_CONFIG };
}

// ==================== DEBUG AND TEST FUNCTIONS ====================

/**
 * Test URL construction
 */
export function debugUrlConstruction(
  filename: string,
  container: string,
  subcontainer?: string | number
): void {
  console.log('🔍 Debug URL Construction:');
  console.log('  Input filename:', filename);
  console.log('  Input container:', container);
  console.log('  Input subcontainer:', subcontainer);

  const mockFile = new File(['test'], 'test.png', { type: 'image/png' });
  const generatedFilename = generateFileName(mockFile, filename, { addTimestamp: true });
  const blobPath = buildBlobPath(container, subcontainer, generatedFilename);

  const baseUrl = `https://${AZURE_CONFIG.storageAccountName}.blob.core.windows.net/${AZURE_CONFIG.containerName}`;
  const finalUrl = `${baseUrl}/${blobPath}`;

  console.log('  Generated filename:', generatedFilename);
  console.log('  Blob path:', blobPath);
  console.log('  Final URL:', finalUrl);
  console.log('  Backend validation:', validateUrlForBackend(finalUrl) ? 'PASS' : 'FAIL');
}

/**
 * Test backend compatibility
 */
export function testBackendCompatibility(): void {
  console.log('🧪 Testing Backend Validation Compatibility:');

  const testUrls = [
    `https://${AZURE_CONFIG.storageAccountName}.blob.core.windows.net/${AZURE_CONFIG.containerName}/system_settings/certificate_logo/certificate_logo_1748508998054.png`,
    `https://${AZURE_CONFIG.storageAccountName}.blob.core.windows.net/${AZURE_CONFIG.containerName}/application_id/12345/demand_note_1748508998054.pdf`,
  ];

  testUrls.forEach(url => {
    const result = validateUrlForBackend(url);
    console.log(`${result ? '✅' : '❌'} ${url}`);
  });
}

// ==================== EXPORTS ====================

// Default export
export default uploadImage;